<i18n>
{
    "uk": {
        "defaultTitle": "Відповіді на деякі важливі запитання"
    },
    "en": {
        "defaultTitle": "Answers to some important questions"
    }
}
</i18n>


<template>
    <div
        v-if="pairs.length > 0 || edit"
        :id="block.id || `${block.type}-${uid}`"
        class="mt-6 mt-md-8 mt-lg-10"
    >
        <div class="page-container">

            <div
                v-if="edit"
                class="edit"
            >
                <button
                    class="btn-bg btn-lg rounded icon-ic-pencil-outline elevate edit-btn"
                    @click="$emit('openFaqEditor')"
                />
            </div>

            <div class="row">
                <div class="col-12 col-lg-10 offset-lg-1 ta-center">

                    <h2 class="h4 h3-sm h2-md mt-0 mb-2 mb-sm-3 mb-md-4" :class="title.class" v-html="title.text || $t('defaultTitle', locale)" />

                    <div
                        v-if="description.text"
                        class="desc mb-2 mt-sm-n1 mb-sm-3 mt-md-n2 mb-md-4"
                        :class="description.class"
                        v-html="description.text"
                    />

                </div>
            </div>

            <div
                class="collapse-accordeon"
                :class="content.class"
            >
                <div
                    v-for="(pair, i) in pairs"
                    :key="`pair-${i}`"
                    class="collapse-panel collapse-panel-plain"
                    :class="pair.class"
                >

                    <CollapseButton
                        class="collapse-header"
                        :target="`faq-question-${i}`"
                        :collapsed="i != 0"
                    >
                        {{ pair.question }}

                        <span class="collapse-state">
                            <span class="collapse-state-icon icon-ic-arrow-simple" :class="pair.iconColor || 'color-gray-0'"></span>
                        </span>
                    </CollapseButton>

                    <Collapse
                        :id="`faq-question-${i}`"
                        group="faq"
                        :collapsed="i != 0"
                    >
                        <div class="text-wrapper rich-text">
                            <p
                                v-for="(p, j) in targettedLinks(pair.answer)"
                                :key="`pair-${i}-p-${j}`"
                                v-html="p"
                            />
                        </div>
                    </Collapse>

                </div>
            </div>

        </div>


        <script v-if="structuredData" type="application/ld+json" v-html="structuredData" />

    </div>
</template>


<script>
    import { useCourseLandingStore } from '~/stores/courseLanding';

    let uid = 0;

    import Collapse       from '~/components/collapse/Collapse.vue';
    import CollapseButton from '~/components/collapse/Button.vue';

    import blockPropertyObject from '~/mixins/course-landing/blockPropertyObject.js';
    import paragraphs from '~/mixins/course-landing/paragraphs.js';

    export default {
        props: {
            block: Object,
            edit:  Boolean,
        },

        components: {
            Collapse,
            CollapseButton,
        },

        mixins: [
            blockPropertyObject,
            paragraphs,
        ],

        data () {
            uid++;

            return {
                uid
            };
        },

        computed: {
            locale () {
                return this.courseLandingStore.locale;
            },

            courseLandingStore () {
                return useCourseLandingStore();
            },


            title () {
                return this.blockPropertyObject(this.block.title, 'text');
            },

            description () {
                return this.blockPropertyObject(this.block.description, 'text');
            },

            content () {
                return this.blockPropertyObject(this.block.content, 'pairs');
            },

            pairs () {
                const content = this.content;

                if (!('pairs' in content)) return [];

                return content.pairs;
            },


            structuredData () {
                return {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": this.pairs.map(pair => {
                        const { question, answer } = pair;
                        return {
                            "@type": "Question",
                            "name": question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": Array.isArray(answer) ? answer.join("\n\n") : answer
                            }
                        };
                    })
                };
            },
        },

        methods: {
            targettedLinks (value) {
                // Make sure the value is not an array
                if (Array.isArray(value)) value = value.join("\n\n");

                /* Regex: remove any square brackets around URLs in href attribute
                 *
                 * This regular expression matches any opening A tag with the
                 * href attribute value wrapped inside square brackets.
                 *
                 * References:
                 *
                 *     $<atts1>          contains any link attributes before the
                 *                       href attribute
                 *
                 *     $<quot>           contains single or double quotes, depending
                 *                       on which ones were used to wrap the href
                 *                       attribute value
                 *
                 *     $<url>            contains link href value without the square
                 *                       brackets
                 *
                 *     $<atts2>          contains any link attributes after the
                 *                       href attribute
                 *
                 * For more details and replacement examples,
                 * see: https://regex101.com/r/Qivyjn/2
                 */
                const regexBrackets = /<a\s+(?<atts1>[^>]*?\s+)?href=(?<quot>["'])\[?(?<url>(?!\k<quot>).+)\]?\k<quot>(?<atts2>[^>]*)>/gi;

                value = value.replace(regexBrackets, '<a $<atts1>href=$<quot>$<url>$<quot>$<atts2>>');

                /* Regex: remove CASES domain from URLs in href attribute
                 *
                 * This regular expression matches any opening A tag with a domain
                 * name present in the href attribute value.
                 *
                 * * References:
                 *
                 *     $<atts1>          contains any link attributes before the
                 *                       href attribute
                 *
                 *     $<quot>           contains single or double quotes, depending
                 *                       on which ones were used to wrap the href
                 *                       attribute value
                 *
                 *     $<url>            contains full link href value
                 *
                 *     $<domain>         contains the domain part of the url
                 *
                 *     $<path>           contains the path part of the url
                 *                       (can be blank)
                 *
                 *     $<atts2>          contains any link attributes after the
                 *                       href attribute
                 *
                 * For more details and examples,
                 * see: https://regex101.com/r/u0nXQx/1
                 */
                const regexDomain = /<a\s+(?<atts1>[^>]*?\s+)?href=(?<quot>["'])(?<url>(?:https?:)?\/\/(?<domain>(?:(?!\k<quot>)[^\/])+)?(?<path>(?:(?!\k<quot>).)+)?)\k<quot>(?<atts2>[^>]*)>/gi;
                const baseURL     = this.$axios.defaults.baseURL;

                value = value.replace(regexDomain, (...args) => {
                    const match      = args.shift();
                    const groups     = args.pop();
                    const { domain } = groups;

                    if (domain != baseURL && domain != `www.${baseURL}`) {
                        // No replacement needed
                        return match;
                    }

                    const { quot, atts1, atts2 } = groups;
                    let { path } = groups;

                    path = path || '/';

                    return `<a ${atts1}href=${quot}${path}${quot}${atts2}>`;
                });

                /* Regex: find all tags without the target="_blank" attribute
                 *
                 * This regular expression matches any opening A tag without the
                 * target="_blank" attribute. This includes links with empty
                 * target attributes or target attributes with values other than
                 * "_blank".
                 *
                 * Pattern details:
                 *
                 *     <a               matches start of the HTML element
                 *
                 *     (?<atts>         opens a capturing group named atts for
                 *                      attribute copying
                 *
                 *     (?:              opens a non-capturing group for an attribute
                 *
                 *     \s+              matches any number of whitespace characters
                 *
                 *     (?!              opens a negative lookahead group
                 *
                 *     target=          matches the target attribute and an equal sign
                 *
                 *     (?<quot>[\"'])   matches either a single or a doulbe quote
                 *                      and captures it with the name quot
                 *
                 *     _blank           matches the _blank attribute value
                 *
                 *     \k<quot>         matches the previously captured group with
                 *                      the name quot
                 *
                 *     )                closes the negative lookahead
                 *
                 *     [^\s>]*          matches any number of characters that are
                 *                      not a whitespace or a >
                 *
                 *     )*               closes a non-capturing group for an attibute
                 *                      and allowes 0+ repeats
                 *
                 *     )                closes a named capturing group for attribute
                 *                      copying
                 *
                 *     >                matches the of the HTML element
                 *
                 * References:
                 *
                 *     $<atts>          contains any link attributes
                 *
                 * For more details and replacement examples,
                 * see: https://regex101.com/r/nFLRDm/4
                 */
                const regexTarget = /<a(?<atts>(?:\s+(?!target=(?<quot>[\"'])_blank\k<quot>)[^\s>]*)*)>/gi;

                value = value.replace(regexTarget, '<a target="_blank"$<atts>>');

                return this.paragraphs(value);
            },
        },
    };
</script>


<style lang="postcss" scoped>
    .desc {
        @media screen and (min-width: 576px) {
            font-size:   19px;
            line-height: 1.263158;
        }

        @media screen and (min-width: 992px) {
            font-size:   24px;
            line-height: 1.166667;
        }
    }

    .edit {
        position: relative;
    }

    .edit-btn {
        position: absolute;
        top:      -6px;
        right:    -8px;
        z-index:  10;
    }


    .collapse-panel-plain {
        margin-right: 0;
        margin-left:  0;

        & + .collapse-panel-plain {
            margin-top:  8px;
            padding-top: 8px;
        }

        .collapse-header {
            font-weight: 800;

            background: transparent;
            transition: background-color .2s ease;

            border-radius: 8px;
            padding:       8px;
            padding-right: 32px;

            @media screen and (min-width: 768px) {
                font-size:   19px;
                line-height: 1.263158;
            }

            &:hover {
                background-color: var(--color-cases-gray-5);
            }

            .collapse-state {
                right: 4px;
            }
        }

        :deep(.collapse-inner) {
            padding:       8px;
            padding-right: 32px;
        }
    }

    .text-wrapper {
        > p:last-child {
            margin-bottom: 0;
        }
    }
</style>
